import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useAuth from './useAuth';

// Used by center users — calls the centerAuthOnly endpoint
export function useCenterCourses() {
  const { request } = useAuth();
  const coursesQuery = useQuery({
    queryKey: ['courses-center'],
    queryFn: () => request.get('/course/getCoursesForCenter').then((res) => res.data),
  });
  return {
    courses: coursesQuery?.data?.courses || [],
    isCoursesLoading: coursesQuery.isLoading,
  };
}

export default function useCourses() {
  const { request } = useAuth(); // this wraps axios with auth headers
  const queryClient = useQueryClient();

  // Fetch courses
  const coursesQuery = useQuery({
    queryKey: ['courses'],
    queryFn: () => request.get('/course/getCourses').then((res) => res.data),
  });

  // Fetch subjects
  const subjectsQuery = useQuery({
    queryKey: ['subjects'],
    queryFn: () => request.get('/course/getSubjects').then((res) => res.data),
  });

  // Add course
  const addCourseMutation = useMutation({
    mutationFn: (data) => request.post('/course/addCourse', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });

  // Update course
  const updateCourseMutation = useMutation({
    mutationFn: (data) => request.put('/course/updateCourse', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });

  // Delete course
  const deleteCourseMutation = useMutation({
    mutationFn: (id) => request.delete('/course/deleteCourse', { data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });

  // Update subject
  const updateSubjectMutation = useMutation({
    mutationFn: (data) => request.put('/course/updateSubject', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
    },
  });

  // Delete subject
  const deleteSubjectMutation = useMutation({
    mutationFn: (id) => request.delete('/course/deleteSubject', { data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] });
    },
  });

  return {
    // Queries
    courses: coursesQuery?.data?.courses || [],
    subjects: subjectsQuery.data || [],
    isCoursesLoading: coursesQuery.isLoading,
    isSubjectsLoading: subjectsQuery.isLoading,

    // Mutations
    addCourse: addCourseMutation.mutateAsync,
    updateCourse: updateCourseMutation.mutateAsync,
    deleteCourse: deleteCourseMutation.mutateAsync,
    updateSubject: updateSubjectMutation.mutateAsync,
    deleteSubject: deleteSubjectMutation.mutateAsync,
    isAddingCourse: addCourseMutation.isPending,
    isUpdatingCourse: updateCourseMutation.isPending,
    isDeletingCourse: deleteCourseMutation.isPending,
  };
}
